import { FC, useEffect, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';
import { Button, Input, Modal, TextField } from '@shared/ui/kit';
import { createPortal } from 'react-dom';
import { SuccessIcon, ErrorIcon, XmarkIcon } from '@shared/assets/icons';
import { typography } from '@shared/tokens/typography.stylex';
import { useFetchUpdateCategory } from '@pages/event/api/useUpdateCategory';
import { useCheckCategoryName } from '@pages/event/api/useCreateCategory';
import { CategoryColorPicker } from '@entities/category';
import { UnstyledButton } from '@shared/ui/unstyled-button';

interface EditCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  countTicket: number;
  categoryId: number;
  initialTitle: string;
  initialDescription: string | null;
  eventId: number;
  initialColor?: string;
}
const LENS_MESSAGE = 200;
const LENS_TITLE = 50;

enum MODAL_STEP {
  edit,
  success,
  error,
}

export const EditCategoryModal: FC<EditCategoryModalProps> = ({
  isOpen,
  onClose,
  countTicket,
  categoryId,
  initialTitle,
  initialDescription,
  eventId,
  initialColor,
}) => {
  const [currentStep, setCurrentStep] = useState(MODAL_STEP.edit);
  const [title, setTitle] = useState(initialTitle);
  const [message, setMessage] = useState(initialDescription || '');
  const [isReadyCheckRequest, setIsReadyCheckRequest] = useState(false);
  const [categoryNameErrors, setCategoryNameErrors] = useState('');
  const [color, setColor] = useState<string | undefined>(initialColor);

  const [{ data: checkCategoryData }] = useCheckCategoryName({
    title,
    eventId,
    pause: !isReadyCheckRequest,
  });
  const { updateCategory } = useFetchUpdateCategory();

  useEffect(() => {
    if (title.trim() === '') {
      setCategoryNameErrors('Название не может быть пустым');
    } else if (title.length > LENS_TITLE) {
      setCategoryNameErrors('Максимальная длинна текста 50 символов');
    } else {
      setCategoryNameErrors('');
    }
  }, [title]);

  useEffect(() => {
    if (checkCategoryData?.checkCategory?.exist && title !== initialTitle) {
      setCategoryNameErrors('Категория с таким названием уже существует');
      setIsReadyCheckRequest(false);
    } else if (isReadyCheckRequest) {
      handleUpdateCategory();
    }
  }, [checkCategoryData]);

  const handleUpdateCategory = async () => {
    if (title.trim() === '') {
      return;
    }

    try {
      const result = await updateCategory({ categoryId, title, description: message || null, color });
      if (result.data?.updateCategory) {
        setCurrentStep(MODAL_STEP.success);
      } else {
        setCurrentStep(MODAL_STEP.error);
      }
    } catch (error) {
      setCurrentStep(MODAL_STEP.error);
    }

    setIsReadyCheckRequest(false);
  };

  const handleClose = () => {
    setCurrentStep(MODAL_STEP.edit);
    onClose();
  };

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (event.target.value.length <= LENS_MESSAGE) {
      setMessage(event.target.value);
    }
  };
  const handleTitleChange = (value: string) => {
    if (value.length <= LENS_TITLE) {
      setTitle(value);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case MODAL_STEP.edit:
        return (
          <div {...stylex.props(styles.container)}>
            <div {...stylex.props(styles.headerContainer)}>
              <p {...stylex.props(styles.title)}>Отредактировать категорию</p>

              <UnstyledButton onClick={handleClose}>
                <XmarkIcon size="24" {...stylex.props(styles.xMarkIcon)} />
              </UnstyledButton>
            </div>

            <div {...stylex.props(styles.inputContainer)}>
              <Input
                labelText="Название *"
                placeholder="Введите название"
                settings={{ width: '432px', height: '36px' }}
                changeFunction={handleTitleChange}
                value={title}
                inputMessage={categoryNameErrors}
                status={categoryNameErrors ? 'error' : undefined}
              />

              <CategoryColorPicker value={color} setValue={setColor} />

              <div {...stylex.props(styles.textareaContainer)}>
                <p {...stylex.props(styles.textCount)}>
                  {message.length}/{LENS_MESSAGE}
                </p>

                <TextField
                  settings={{ width: '432px', height: '78px', resize: 'vertical' }}
                  placeholder="Введите описание"
                  labelMsg="Описание"
                  changeFunction={handleDescriptionChange}
                  value={message}
                />
              </div>
            </div>
            {countTicket > 0 && (
              <div {...stylex.props(styles.info)}>В категорию добавлено {countTicket} участников</div>
            )}

            <div {...stylex.props(styles.action)}>
              <Button
                onClick={onClose}
                variant="secondary"
                size="medium"
                settings={{ height: '36px', width: '85px', fontSize: typography['fontSizeCaption-1'] }}
              >
                Отмена
              </Button>
              <Button
                onClick={() => setIsReadyCheckRequest(true)}
                isDisabled={!!categoryNameErrors}
                variant="accent"
                size="medium"
                settings={{ height: '36px', width: '103px', fontSize: typography['fontSizeCaption-1'] }}
              >
                Сохранить
              </Button>
            </div>
          </div>
        );

      case MODAL_STEP.success:
        return (
          <div {...stylex.props(styles.container)}>
            <p {...stylex.props(styles.header)}>Отредактировать категорию</p>
            <div {...stylex.props(styles.successInfo)}>
              <div {...stylex.props(styles.icon)}>
                <SuccessIcon />
              </div>
              <p {...stylex.props(styles.description)}>Категория успешно изменена</p>
            </div>
            <div {...stylex.props(styles.action)}>
              <Button
                onClick={handleClose}
                variant="accent"
                size="medium"
                settings={{ height: '36px', width: '90px', fontSize: typography['fontSizeCaption-1'] }}
              >
                Закрыть
              </Button>
            </div>
          </div>
        );

      case MODAL_STEP.error:
        return (
          <div {...stylex.props(styles.container)}>
            <p {...stylex.props(styles.header)}>Ошибка</p>
            <div {...stylex.props(styles.successInfo)}>
              <div {...stylex.props(styles.icon)}>
                <ErrorIcon />
              </div>

              <p {...stylex.props(styles.description)}>
                Во время редактирования категории произошла непредвиденная ошибка, повторите действие позднее
              </p>
            </div>
            <div {...stylex.props(styles.action)}>
              <Button
                onClick={handleClose}
                variant="accent"
                size="medium"
                settings={{ height: '36px', width: '90px', fontSize: typography['fontSizeCaption-1'] }}
              >
                Закрыть
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return createPortal(
    <Modal
      active={isOpen}
      settingsHeader={{ height: currentStep === MODAL_STEP.edit ? '0' : '16px', padding: '0px' }}
      settingsContainer={{
        height: 'auto',
        width: '480px',
        padding: '0 24px',
      }}
      settingsFooter={{ height: '16px', padding: '0px' }}
    >
      {renderStep()}
    </Modal>,
    document.getElementById('modal') as Element
  );
};
